/**
 * Brand Setup's step model — one decision per screen (spec §2.2).
 *
 * This is the PURE spine of the stepper: which screens a lane shows, how they
 * are numbered, where Continue and Back go, and the words each screen says.
 * The screens render it; nothing here imports React, `state.ts` or the DOM, so
 * the whole navigation contract is unit-tested in node.
 *
 * Three rules the rest of the wizard leans on:
 *
 *  1. **Counted ≠ visible.** `welcome`, `reading` and `done` are screens but
 *     not decisions, so the counter reads "Step 7 of 10" and not "of 13". The
 *     automatic lane adds exactly ONE counted screen (`sources`) — `reading`
 *     is a progress screen, and promising the operator an extra step for
 *     watching a spinner would be a lie.
 *  2. **The lane decides the list, never the screen.** `visibleSteps` is the
 *     single source of truth for order, so the rail, the counter and
 *     `nextStep`/`prevStep` can never disagree about what comes next.
 *  3. **`returnTo` beats everything.** A "Change" from Review sets it, and
 *     BOTH Continue and Back honour it — the operator who came from Review
 *     goes back to Review, whichever button they reach for (§2.1).
 *
 * The step ids are the vocabulary of the whole sub-project: `draft.ts`
 * persists one, the rail marks them done, and the study's chips are mapped
 * onto them by `studiedStepsWithChips`.
 */

/** Every screen Brand Setup can show, in walk order. */
export type StepId =
  | "welcome"
  | "basics"
  | "path"
  | "sources"
  | "reading"
  | "logo"
  | "colors"
  | "font"
  | "captionSize"
  | "captionPosition"
  | "speaker"
  | "signoff"
  | "review"
  | "done";

/** The four groups the rail collapses the steps into. */
export type ChapterId = "basics" | "look" | "captions" | "finish";

export const CHAPTERS: ReadonlyArray<{ id: ChapterId; label: string }> = [
  { id: "basics", label: "Basics" },
  { id: "look", label: "Look" },
  { id: "captions", label: "Captions" },
  { id: "finish", label: "Finish" },
];

export interface StepDef {
  id: StepId;
  /** null = a screen that lives outside the chapters (welcome, done). */
  chapter: ChapterId | null;
  /** The rail's short name for it. `STEP_COPY` carries the same string. */
  label: string;
  /** Counted screens are DECISIONS — they get a number in "Step n of N". */
  counted: boolean;
}

/**
 * The lane, as everything here needs to see it. `BrandSetupState` satisfies it
 * structurally, so callers pass the state itself and no import is needed in
 * either direction (`state.ts` imports `StepId` from here — never the reverse).
 */
export interface StepLane {
  mode: "auto" | "manual" | null;
  /** True once "Keep all and continue" was taken on `reading` (§2.2). */
  keptAll?: boolean;
}

/** Every step, in canonical order. `visibleSteps` filters this — the order is
 *  defined ONCE so a lane can never re-order the walk. */
const ALL_STEPS: readonly StepDef[] = [
  { id: "welcome", chapter: null, label: "Welcome", counted: false },
  { id: "basics", chapter: "basics", label: "Basics", counted: true },
  { id: "path", chapter: "basics", label: "Style", counted: true },
  { id: "sources", chapter: "basics", label: "Sources", counted: true },
  { id: "reading", chapter: "basics", label: "Reading", counted: false },
  { id: "logo", chapter: "look", label: "Logo", counted: true },
  { id: "colors", chapter: "look", label: "Colors", counted: true },
  { id: "font", chapter: "look", label: "Font", counted: true },
  { id: "captionSize", chapter: "captions", label: "Size", counted: true },
  { id: "captionPosition", chapter: "captions", label: "Position", counted: true },
  { id: "speaker", chapter: "captions", label: "Speaker", counted: true },
  { id: "signoff", chapter: "captions", label: "Text", counted: true },
  { id: "review", chapter: "finish", label: "Review", counted: true },
  { id: "done", chapter: null, label: "Done", counted: false },
];

/** The two screens only the automatic lane shows. */
const STUDY_ONLY: ReadonlySet<StepId> = new Set<StepId>(["sources", "reading"]);

/**
 * The screens THIS lane walks.
 *
 * `mode: null` (the lane is not picked yet, i.e. the operator is on or before
 * `path`) shows the manual list: the rail must not promise a Sources screen
 * that a "Start from our defaults" click would take away again.
 */
export function visibleSteps(s: Pick<StepLane, "mode">): StepDef[] {
  return s.mode === "auto" ? [...ALL_STEPS] : ALL_STEPS.filter((d) => !STUDY_ONLY.has(d.id));
}

/** The decisions — visible ∧ counted. What "Step n of N" counts. */
export function countedSteps(s: Pick<StepLane, "mode">): StepDef[] {
  return visibleSteps(s).filter((d) => d.counted);
}

/** 1-based position among this lane's decisions, or null when the step is
 *  uncounted (welcome / reading / done) or not on this lane at all. */
export function stepNumber(s: Pick<StepLane, "mode">, id: StepId): { n: number; total: number } | null {
  const counted = countedSteps(s);
  const i = counted.findIndex((d) => d.id === id);
  return i < 0 ? null : { n: i + 1, total: counted.length };
}

/** The canonical position of a step, lane-independent — the anchor both
 *  walkers use so an id the lane hides still knows where it sat. */
function canonicalIndex(id: StepId): number {
  return ALL_STEPS.findIndex((d) => d.id === id);
}

/**
 * Where Continue goes.
 *
 * Order of precedence, and it matters:
 *   1. `returnTo` — the operator came from Review, so Continue goes back
 *      there. (Clearing it is the reducer's job: `{ type: "step" }` without a
 *      `returnTo` clears the flag, so every ordinary jump resets it.)
 *   2. Keep all — `reading` hands straight to `logo` (the one step the study
 *      can never answer: it does not invent a logo image), and `logo` then
 *      hands to `review`. `keepAll` may arrive as the option (the click that
 *      is happening) or as `keptAll` on the state (the click that already
 *      happened one screen ago).
 *   3. The next visible step.
 */
export function nextStep(
  s: StepLane,
  id: StepId,
  opts?: { keepAll?: boolean; returnTo?: StepId | null },
): StepId {
  if (opts?.returnTo) return opts.returnTo;
  const keepAll = opts?.keepAll === true || s.keptAll === true;
  if (id === "reading" && keepAll) return "logo";
  if (id === "logo" && keepAll) return "review";
  const visible = visibleSteps(s);
  const here = canonicalIndex(id);
  const next = visible.find((d) => canonicalIndex(d.id) > here);
  return next?.id ?? visible[visible.length - 1]!.id;
}

/**
 * Where Back goes. `returnTo` wins for the same reason it wins on Continue.
 * `reading` is pinned to `sources` explicitly: it is the one screen whose Back
 * is a question ("Stop reading and go back to your sources?"), and the answer
 * has to be its own sources, never wherever the visible list happens to put it.
 * Never walks off the front — `welcome` is the floor.
 */
export function prevStep(s: StepLane, id: StepId, opts?: { returnTo?: StepId | null }): StepId {
  if (opts?.returnTo) return opts.returnTo;
  if (id === "reading") return "sources";
  const visible = visibleSteps(s);
  const here = canonicalIndex(id);
  const before = visible.filter((d) => canonicalIndex(d.id) < here);
  return before[before.length - 1]?.id ?? "welcome";
}

// ---------------------------------------------------------------------------
// Study findings → steps
// ---------------------------------------------------------------------------

/**
 * The steps a study can prefill. Everything else it finds (darkening, line
 * height, pacing…) has no screen and shows on Review as "…and N more we
 * applied" — the spec's rule that a finding is only "counted" when the
 * operator will actually see it go by.
 */
export const STUDIED_STEPS: ReadonlySet<StepId> = new Set<StepId>([
  "logo",
  "colors",
  "font",
  "captionSize",
  "captionPosition",
  "speaker",
  "signoff",
]);

/** chip.field → the step that shows it. Prefix entries end in `.`. */
const CHIP_STEPS: ReadonlyArray<{ match: string; prefix: boolean; step: StepId }> = [
  // The study never invents a logo IMAGE, but it does read where the reel's
  // logo sits — and the logo step shows that as the placeholder's spot
  // (round-2 §2.3), so it is a studied step like any other.
  { match: "layout.logo.", prefix: true, step: "logo" },
  { match: "brand.captionColor", prefix: false, step: "colors" },
  { match: "brand.captionAccent", prefix: false, step: "colors" },
  { match: "speakerNameColor", prefix: false, step: "colors" },
  { match: "fontRoles.", prefix: true, step: "font" },
  { match: "layout.caption.fontSizePx", prefix: false, step: "captionSize" },
  { match: "layout.caption.bottomPx", prefix: false, step: "captionPosition" },
  // `justify` is the other half of the same decision (§2.3: the position step
  // writes both), so a study that moved the captions is reported by the step
  // that shows the move, not as an anonymous "and N more".
  { match: "layout.caption.justify", prefix: false, step: "captionPosition" },
  { match: "layout.speakerCard.", prefix: true, step: "speaker" },
  { match: "layout.overlays", prefix: false, step: "signoff" },
];

/** The step a single chip belongs to, or null when no step shows it. */
export function stepForChipField(field: string): StepId | null {
  for (const e of CHIP_STEPS)
    if (e.prefix ? field.startsWith(e.match) : field === e.match) return e.step;
  return null;
}

/**
 * Which studied steps a chip list covers — returned in STEP ORDER, not chip
 * order, so the rail and Review can mark them in the order the operator will
 * meet them. Deduplicated: two font chips are still one Font step.
 */
export function studiedStepsWithChips(chips: ReadonlyArray<{ field: string }>): StepId[] {
  const hit = new Set<StepId>();
  for (const c of chips) {
    const step = stepForChipField(c.field);
    if (step && STUDIED_STEPS.has(step)) hit.add(step);
  }
  return ALL_STEPS.filter((d) => hit.has(d.id)).map((d) => d.id);
}

// ---------------------------------------------------------------------------
// Copy
// ---------------------------------------------------------------------------

/**
 * What a headline may need to know about the state. The logo step greets a
 * drawn wordmark differently from an uploaded logo or an empty step, and takes
 * `logoKind` straight off the state rather than a boolean a caller has to
 * derive (and can derive wrongly); `studyFoundLogo` is `state.ts`'s verdict of
 * the same name — the study saw a logo on the reel and the placeholder is
 * sitting in its spot.
 */
export interface StepCtx {
  logoKind?: "upload" | "wordmark" | null;
  studyFoundLogo?: boolean;
}

export interface StepCopy {
  label: string;
  /** The brand name is interpolated, so a screen never says "your channel". */
  headline: (brand: string, ctx?: StepCtx) => string;
  /** The one sentence under the headline saying WHY we are asking. */
  why: string;
  /** A state-dependent why-sentence; when present and it returns a string,
   *  it replaces `why`. Only the logo step has one (a studied logo changes
   *  what the screen is asking). */
  whyFor?: (ctx?: StepCtx) => string | null;
}

/** The name to use when the operator has not typed one yet. */
function brandOr(brand: string, fallback: string): string {
  return brand.trim() || fallback;
}

/**
 * Every screen's words — spec §2.2, verbatim. Sentences, not labels; no units
 * in a headline; "your brand", never "channel"; and never a promised duration.
 *
 * `reading` and `done` carry no why-sentence: the spec gives them none because
 * neither is a question — `reading` shows four progress tiles with their own
 * sentences, and `done` shows two buttons. An invented sentence there would be
 * copy nobody wrote.
 */
export const STEP_COPY: Record<StepId, StepCopy> = {
  welcome: {
    label: "Welcome",
    headline: () => "Let's set up your brand.",
    why: "A few questions, one at a time. You'll see every choice on a sample video — and you can change all of it later.",
  },
  basics: {
    label: "Basics",
    headline: () => "Let's start with the basics.",
    why: "We'll put the name on your videos. Captions come out in the language you pick — even when the speaker talks in another.",
  },
  path: {
    label: "Style",
    headline: () => "Want us to copy a style you like?",
    why: "Paste one of your reels — or one you admire — and we match its colors, fonts and captions. Or start from ours.",
  },
  sources: {
    label: "Sources",
    headline: () => "Paste the reel you like.",
    why: "One reel is enough. More is better.",
  },
  reading: {
    label: "Reading",
    headline: () => "Reading your brand…",
    why: "",
  },
  logo: {
    label: "Logo",
    // A studied logo comes first: the placeholder is in the reel's spot and
    // the screen is asking for the file that replaces it. An upload is the
    // one thing that outranks it — the spot is already taken.
    headline: (_brand, ctx) =>
      ctx?.studyFoundLogo && ctx.logoKind !== "upload"
        ? "We saw a logo on your reel."
        : ctx?.logoKind === "wordmark"
          ? "Here's your name on a video."
          : "Add your logo.",
    why: "Have a logo? Drop it here and it takes the wordmark's place. A PNG with a transparent background looks best.",
    whyFor: (ctx) =>
      ctx?.studyFoundLogo && ctx.logoKind !== "upload"
        ? "Add it here and it takes the placeholder's spot. Continue without one and we'll use your name."
        : null,
  },
  colors: {
    label: "Colors",
    headline: () => "Pick your colors.",
    why: "Captions use your text color. The one word that matters gets the highlight — so does the speaker's name.",
  },
  font: {
    label: "Font",
    headline: () => "Choose your caption font.",
    why: "This is the face your captions are set in.",
  },
  captionSize: {
    label: "Size",
    headline: () => "How big should captions be?",
    why: "Bigger reads faster on a phone. Smaller fits more per line.",
  },
  captionPosition: {
    label: "Position",
    headline: () => "Where should captions sit?",
    why: "Middle keeps captions on the face; lower keeps the face clear.",
  },
  speaker: {
    label: "Speaker",
    headline: () => "Where should the speaker's name go?",
    why: "We detect who's talking and show their name for the first few seconds.",
  },
  // The id stays `signoff` (drafts, the rail, tests); the screen grew from one
  // line into every line burned into the video (round-2 §2.4).
  signoff: {
    label: "Text",
    headline: () => "Text on every video?",
    why: "Lines burned into every video — a handle, a call to action. Keep, change or remove what we found.",
  },
  review: {
    label: "Review",
    headline: (brand) => `Here's ${brandOr(brand, "your brand")}.`,
    why: "Change anything before we save it.",
  },
  done: {
    label: "Done",
    headline: (brand) => `${brandOr(brand, "Your brand")} is set.`,
    why: "",
  },
};
